/**
 * Cache localStorage para datos pesados del dashboard de cartera.
 *
 * UX:
 *  - Sin caché → loading global (placeholder grande)
 *  - Con caché → muestra datos viejos + "Actualizado HH:MM" + spinner discreto
 *  - TTL: 1h (datos cambian poco, mejor responsividad)
 */
import { Injectable } from '@angular/core';

interface CacheEntry<T> {
  data: T;
  updated_at: number;   // epoch ms
  ttl_ms: number;
}

@Injectable({ providedIn: 'root' })
export class CarteraCacheService {
  private readonly PREFIX = 'cartera_cache_v3:';   // bump al renombrar F1 (sacar "engañosa")
  private readonly DEFAULT_TTL_MS = 60 * 60 * 1000; // 1h

  get<T>(key: string): { data: T; updated_at: number; is_stale: boolean } | null {
    try {
      const raw = localStorage.getItem(this.PREFIX + key);
      if (!raw) return null;
      const entry: CacheEntry<T> = JSON.parse(raw);
      const age = Date.now() - entry.updated_at;
      const is_stale = age > entry.ttl_ms;
      return { data: entry.data, updated_at: entry.updated_at, is_stale };
    } catch {
      return null;
    }
  }

  set<T>(key: string, data: T, ttl_ms = this.DEFAULT_TTL_MS): void {
    try {
      const entry: CacheEntry<T> = { data, updated_at: Date.now(), ttl_ms };
      localStorage.setItem(this.PREFIX + key, JSON.stringify(entry));
    } catch (e) {
      // QuotaExceeded: limpiar cache vieja
      console.warn('[CarteraCache] localStorage full, clearing old entries', e);
      this.clearOld();
      try { localStorage.setItem(this.PREFIX + key, JSON.stringify({ data, updated_at: Date.now(), ttl_ms })); } catch {}
    }
  }

  invalidate(key: string): void {
    localStorage.removeItem(this.PREFIX + key);
  }

  invalidateAll(): void {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(this.PREFIX));
    keys.forEach(k => localStorage.removeItem(k));
  }

  private clearOld(): void {
    const keys = Object.keys(localStorage).filter(k => k.startsWith(this.PREFIX));
    // Borrar las 50% más antiguas
    const entries: Array<[string, number]> = [];
    for (const k of keys) {
      try {
        const e = JSON.parse(localStorage.getItem(k) || '{}');
        entries.push([k, e.updated_at || 0]);
      } catch {}
    }
    entries.sort((a, b) => a[1] - b[1]);
    const toDrop = Math.ceil(entries.length / 2);
    for (let i = 0; i < toDrop; i++) localStorage.removeItem(entries[i][0]);
  }

  formatTimestamp(epochMs: number): string {
    if (!epochMs) return '—';
    const d = new Date(epochMs);
    const today = new Date();
    const sameDay = d.toDateString() === today.toDateString();
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return sameDay
      ? `Actualizado ${hh}:${mm}`
      : `Actualizado ${d.toLocaleDateString('es-VE', { day: '2-digit', month: 'short' })} ${hh}:${mm}`;
  }
}
